import React, { memo, useEffect, useMemo, useRef } from "react";
import Markdown, { defaultUrlTransform } from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
// remarkBreaks removed: it converted every single \n to <br>, breaking normal
// markdown paragraph flow. Standard markdown treats \n\n as paragraph breaks
// and single \n as continuation — which is what we want for LLM output.
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { stripInternalUUIDs } from "@/common/lib/sanitize-uuids";

type Props = {
  /** Raw assistant text (Markdown-ish) */
  content: string;
  /** Optional: add a class on the wrapper */
  className?: string;
  /** Optional inline element rendered after the markdown output (e.g. a copy button) */
  trailingInline?: React.ReactNode;
  /** Workspace module labels that should be clickable when rendered in bold */
  moduleLabels?: string[];
  /** Called when a clickable module label is clicked */
  onModuleLabelClick?: (label: string, occurrenceIndex: number) => void;
  /** Called when a workspace object link (xavior://object/type/id) is clicked */
  onObjectClick?: (objectType: string, objectId: string) => void;
  /** Called when an autolinked email address is clicked. Receives the bare address (no mailto:). */
  onEmailClick?: (email: string) => void;
  /** Called when an autolinked phone number is clicked. Receives the bare number (no tel:). */
  onPhoneClick?: (phone: string) => void;
};

// Strip ``` / ~~~ code-fence delimiter lines from assistant prose, keeping the
// inner content as normal markdown. The assistant occasionally wraps contact
// listings (and other prose) in a fence; rendering that as a monospace code box
// reads as "code" to users, and during STREAMING an unclosed fence would
// otherwise swallow the rest of the message into one growing code box — the
// visible "stall". Dropping the delimiter lines (complete or dangling) keeps the
// content as human-readable text whose markdown (bold, links, lists) still
// renders. Only true fence lines (start-of-line, ≤3 leading spaces) are removed,
// so inline ``code`` and indented fences inside lists are untouched.
function stripCodeFences(text: string): string {
  if (!text.includes("```") && !text.includes("~~~")) return text;
  return text
    .split("\n")
    .map((line) => {
      // Fence line: optional ≤3 spaces, the ``` / ~~~ marker, then its info
      // string (the language token, no spaces). Drop the marker + language but
      // KEEP any prose that followed on the SAME line — the assistant sometimes
      // emits "```markdown Contact: Vincent", and filtering the whole line would
      // silently delete "Contact: Vincent". Returns null for a bare fence line.
      const m = line.match(/^\s{0,3}(?:```|~~~)(\S*)(.*)$/);
      if (!m) return line;
      const trailing = m[2].replace(/^\s+/, "");
      return trailing.length > 0 ? trailing : null;
    })
    .filter((line): line is string => line !== null)
    .join("\n");
}

function safeUrlTransform(url: string): string {
  const safe = defaultUrlTransform(url);
  if (url.startsWith("tel:")) return url;
  if (url.startsWith("xavior://")) return url;
  return safe;
}

/** Parse xavior://object/{type}/{id} links. Returns null if not a match. */
function parseObjectLink(href: string): { objectType: string; objectId: string } | null {
  const match = href.match(/^xavior:\/\/object\/([a-z]+)\/(.+)$/);
  if (!match) return null;
  return { objectType: match[1], objectId: match[2] };
}

function isProbablyExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

/** @internal Exported for unit tests only — not part of the public API. */
export function _normalizePlainAssistantSegment(input: string): string {
  return normalizePlainAssistantSegment(input);
}

// Collapse a `**bold**` span whose markers are split across a soft line break
// (e.g. `**Summary\n**`). Under CommonMark a `**` adjacent to a newline is not
// flanking and can't open/close emphasis, so it renders as literal asterisks.
// Limited to short spans (≤2 newlines, ≤120 chars) so we don't merge genuinely
// separate paragraphs that merely sit between two bold spans (issue #3037).
function collapseLineBrokenBold(input: string): string {
  return input.replace(/\*\*([\s\S]*?)\*\*/g, (match, inner: string) => {
    if (!inner.includes('\n')) return match; // already single-line, leave alone
    const newlineCount = (inner.match(/\n/g) || []).length;
    if (newlineCount > 2 || inner.length > 120) return match; // too long, likely mismatched
    const collapsed = inner.replace(/\s*\n\s*/g, ' ').trim();
    if (!collapsed) return match; // empty bold, leave alone
    return `**${collapsed}**`;
  });
}

// The model frequently separates a heading, a bold section-label, and a body
// paragraph with a SINGLE newline. CommonMark treats a lone `\n` as a soft break
// (collapsed to a space), so those blocks glue together — a bold heading runs
// into its paragraph, or (worst case) the whole answer renders as one block.
// Promote a lone `\n` to a paragraph break, but ONLY between two block-level
// prose lines: the first line must end a block (sentence-terminal punctuation,
// an ATX `#` heading, or a standalone `**bold**` heading), and neither line may
// be a list item, table row, blockquote, indented/fenced code, or a line with an
// unclosed bold span (a wrapped continuation). This leaves genuine markdown
// structure — and already-`\n\n`-separated paragraphs — untouched.
function promoteParagraphNewlines(input: string): string {
  if (!input.includes('\n')) return input;
  const lines = input.split('\n');
  const isStructural = (s: string): boolean => {
    const t = s.trimStart();
    return (
      /^([-*+•]\s|\d{1,3}[.)]\s|\||>)/.test(t) || // list / table / blockquote
      /^ {4,}\S/.test(s) // indented code
    );
  };
  const out: string[] = [];
  let inFence = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    out.push(line);
    if (/^\s*(```|~~~)/.test(line)) inFence = !inFence;
    if (inFence) continue;
    const next = lines[i + 1];
    if (next === undefined) continue;
    if (line.trim() === '' || next.trim() === '') continue; // already a blank boundary
    if (isStructural(line) || isStructural(next)) continue; // protect lists/tables/quotes/code
    if ((line.match(/\*\*/g) || []).length % 2 === 1) continue; // wrapped/unclosed bold
    // The model writes each heading/paragraph on its own line and does NOT
    // soft-wrap, so a lone newline is a paragraph boundary — UNLESS the next line
    // starts lowercase, which signals a wrapped sentence continuation. New blocks
    // (headings, bold labels, new sentences) start with #, *, a digit, or a capital.
    if (/^[a-z]/.test(next.trimStart())) continue;
    out.push(''); // promote the lone newline to a paragraph break
  }
  return out.join('\n');
}

function normalizePlainAssistantSegment(input: string): string {
  if (!input) return input;
  let output = input;

  // Fix headings missing space after #: "#Header" → "# Header", "##Title" → "## Title"
  output = output.replace(/^(#{1,6})([^\s#])/gm, "$1 $2");

  // Ensure markdown headings (##) get a paragraph break before them.
  // Headings need a blank line before them to be parsed as headings by CommonMark.
  output = output.replace(/([^\n])\n(#{1,6}\s)/g, "$1\n\n$2");
  // Also handle headings with no preceding newline at all (glued to text).
  output = output.replace(/([^\n\s#])\s*(#{1,6}\s)/g, "$1\n\n$2");

  // `---` thematic-break dividers that the model glues to the end of a prose
  // line (e.g. "...mass slaughter.---") otherwise render as literal trailing
  // dashes. Pull a run of 3+ hyphens onto its own line with blank lines around
  // it so CommonMark renders a real <hr>. Require a non-space char immediately
  // before AND end-of-line right after, so we never disturb a standalone `---`
  // line, a setext-heading underline (alone under its text), or an inline em-dash
  // / hyphenated word ("well---known" keeps text after the dashes, so no match).
  output = output.replace(/(\S)[ \t]*-{3,}[ \t]*(?=\n|$)/g, "$1\n\n---\n\n");

  // Fix bold markers split across a soft line break (e.g. `**Summary\n**`).
  // Run here so later structural rules see well-formed bold; also run again at
  // the very end, because some \n\n-insertion rules below can re-break a pair.
  output = collapseLineBrokenBold(output);

  // Fix bold markers with extra whitespace: "** text**" or "**text **" → "**text**"
  // Must run before italic fixes to avoid single-* regexes eating ** boundaries.
  // Use lookbehind to ensure the opening ** isn't preceded by a word char (which would mean it's closing **).
  // Use [^\S\n] (horizontal whitespace only) to avoid matching across newlines.
  output = output.replace(/(?<!\w)\*\*[^\S\n]+([^*\n]{1,50}?)[^\S\n]*\*\*(?!\*)/g, (_, inner: string) => `**${inner.trim()}**`);
  output = output.replace(/(?<!\w)\*\*([^*\s\n][^*\n]{0,50}?)[^\S\n]+\*\*(?!\*)/g, (_, inner: string) => `**${inner.trim()}**`);

  // Fix italic markers with extra whitespace: "* text*" or "*text *" → "*text*"
  // Use lookbehind/lookahead to avoid matching * that is part of ** bold markers.
  output = output.replace(/(?<!\*)\*\s+([^*\n][^*\n]*?)\s*\*(?!\*)/g, (_, inner: string) => `*${inner.trim()}*`);
  output = output.replace(/(?<!\*)\*([^*\n]+?)\s+\*(?!\*)/g, (_, inner: string) => `*${inner.trim()}*`);

  // Inline bold "definition labels" with the colon OUTSIDE the bold — haiku's
  // flowing style: "...genuinely idealistic. **Path to power**: He joined the
  // Jacobin Club... **The Terror**: His conviction..." glues several labelled
  // sections into one wall. Also handles em-dash/en-dash variants: "**Area
  // codes & trivia** — description" (issue #3386). Promote each to its own
  // paragraph. Gate hard to a real section label: must follow a sentence
  // terminator + space, the bold must start uppercase and be short (≤48 chars),
  // and a separator (colon, em-dash, or en-dash) must follow the close `**` —
  // so ordinary inline emphasis ("...the **general will**: a note") is left
  // untouched (it isn't preceded by a sentence terminator).
  // MUST run before the bold-heading-glue rule below: once each label is split
  // onto its own paragraph, that rule can no longer mis-pair one label's closing
  // `**` with the next label's opening `**` (the `[^*\n]` span can't cross \n).
  output = output.replace(
    /([.!?])[ \t]+(\*\*[A-Z][^*\n]{1,48}\*\*[ \t]*[:—–])/g,
    "$1\n\n$2",
  );

  // Fix dash glued to bold markers in list items: "-**text**" → "- **text**"
  output = output.replace(/^(\s*)-\*\*/gm, '$1- **');

  // Fix unspaced dash list items: "-item" → "- item" (only at line start)
  output = output.replace(/^(\s*)-([^\s\d-])/gm, '$1- $2');

  // Fix lone "-*" or "-*\n" at line start (broken list+italic combo) — normalize to "- "
  output = output.replace(/^(\s*)-\*\s*$/gm, '$1-');
  output = output.replace(/^(\s*)-\*\s+/gm, '$1- ');

  // Fix first list item glued to intro text via dash/en-dash:
  // "You have 4 contacts:– Betty" or "contacts:- Betty" → proper list
  output = output.replace(/(:)[–—-]\s*/g, '$1\n\n- ');

  // Fix first list item glued to intro text without dash (just newline missing before first bullet):
  // "at Greylock:\n- Name" should have a blank line; "at Greylock:Name" needs ":\n\n- Name"
  // Already handled by markdown rules if there's a newline, but catch the no-newline case:
  output = output.replace(/(:\s*)(\*\*[^*\n]+\*\*)\n(\s*[-•])/g, '$1\n\n- $2\n$3');

  // Fix trailing text merged into last bullet item. The LLM often appends a follow-up
  // sentence to the last list item instead of putting it in a new paragraph.
  // Detect sentence-start phrases after list item content and split into new paragraph.
  const trailingPhrases = 'Do you|Want me|Would you|You also|You have|You\'re|Is there|Should I|That\'s|I also|I can|I don\'t|There\'?s also|Any |Need |Let me|Shall I|Happy to|Want to|Note:|Additionally';
  output = output.replace(
    new RegExp(`^(\\s*[-•]\\s+.{5,}?)\\s+((?:${trailingPhrases})[^\\n]*)`, 'gm'),
    '$1\n\n$2',
  );

  // Strip standalone * or ** or *** on their own line (orphaned markers).
  output = output.replace(/^\s*\*{1,3}\s*$/gm, '');

  // Normalize *** (triple star) — usually a mangled bold-italic attempt.
  // "**text ***" → "**text**", "***text**" → "**text**"
  output = output.replace(/\*\*([^*\n]+?)\s*\*{3,}/g, '**$1**');
  output = output.replace(/\*{3,}([^*\n]+?)\*\*/g, '**$1**');

  // Strip ** or * at start of line when followed by whitespace (orphaned opening markers).
  output = output.replace(/^(\s*)\*{1,2}\s+(?!\*)/gm, '$1');

  // Strip lone ** in the middle of text that aren't valid bold markers.
  // Pattern: punctuation, then whitespace, then **, then space — these are orphaned opening **.
  // Require whitespace before ** to avoid stripping closing ** like "Jr.**".
  output = output.replace(/([.!?:,;])\s+\*\*\s+/g, '$1 ');

  // Strip ** that wraps multi-line sections (>2 lines between ** pairs).
  // These are never valid bold — markdown bold must be inline.
  output = output.replace(/\*\*([\s\S]*?)\*\*/g, (match, inner: string) => {
    const newlines = (inner.match(/\n/g) || []).length;
    if (newlines <= 2) return match; // short spans are handled elsewhere
    return inner; // strip the ** wrapper, keep content
  });

  // Fix orphaned opening ** at end of text (streaming partial) — remove them.
  // Only strip if the ** count (including trailing) is odd, meaning the last ** is unmatched.
  if (/\*\*\s*$/.test(output)) {
    const allCount = (output.match(/\*\*/g) || []).length;
    if (allCount % 2 === 1) {
      output = output.replace(/\*\*\s*$/, '');
    }
  }

  // Strip orphaned ** at start of line (with optional bullet prefix) with no matching close on same line.
  output = output.replace(/^(\s*(?:-\s+)?)\*\*(?!\*)(\S[^*\n]*?)$/gm, (match, prefix: string, content: string) => {
    // Only strip if this line has an odd number of ** (unmatched opening)
    const starPairs = (match.match(/\*\*/g) || []).length;
    if (starPairs % 2 === 1) return `${prefix}${content}`;
    return match;
  });

  // Strip orphaned closing ** mid-line (no opening ** earlier on the same line).
  // e.g., "- World Wide Web invented** by Tim" → "- World Wide Web invented by Tim"
  output = output.replace(/^([^*\n]+?)\*\*(\s+\S)/gm, (match, before: string) => {
    // Only strip if there's no opening ** before this closing **
    if (before.includes('**')) return match;
    return match.replace('**', '');
  });

  // Strip orphaned **- or :**- patterns on the SAME line (bold marker glued to colon/dash).
  // e.g., "World Leaders:**-" → "World Leaders:"
  // Use [^\S\n]* to avoid crossing newlines (which would merge section headers with bullets).
  output = output.replace(/:\*\*[^\S\n]*-/g, ':');

  // Insert missing separators before glued ordered list markers (e.g., "modules1. X").
  output = output.replace(/([^\n\d])(?=(\d{1,3}\.(?:\s+)?(?:\*\*|[^\s\d])))/g, "$1\n");
  output = output.replace(/(^|[\s(])(\d{1,3})\.(?=[^\s\d])/gm, "$1$2. ");

  // Ensure section labels and numbered lists are separated.
  output = output.replace(/(:)\s*(\d{1,3}\.\s+)/g, "$1\n$2");
  // Break module count summaries into clean lines (e.g., "Tasks:20 tasks").
  output = output.replace(/(:)\s*([A-Z][A-Za-z0-9'’ _-]{1,80}:\s*\d+)/g, "$1\n$2");
  // Split glued bullets after count summaries.
  output = output.replace(/(\b\d+\s+[A-Za-z][A-Za-z ]{0,20})(\s*-\s+[A-Z])/g, "$1\n$2");

  // Promote single-newline-separated prose blocks (heading / bold label / body)
  // into real paragraph breaks. The model routinely uses lone `\n` where markdown
  // needs `\n\n`, which otherwise renders the whole answer as one glued block.
  output = promoteParagraphNewlines(output);

  // Strip internal UUIDs and IDs from user-visible text (#4615).
  // Logic lives in the shared sanitize-uuids helper (also used to sanitize the
  // persisted `thinking` field in message-mapping.ts).
  output = stripInternalUUIDs(output);

  // Remove assistant-only parenthetical disambiguators from visible text.
  output = output.replace(/\s*\(\s*option\s+\d+\s*\)/gi, "");
  output = output.replace(/\s*\(\s*attached to this conversation\s*\)/gi, "");
  output = output.replace(/\s*\(\s*attached here\s*\)/gi, "");
  output = output.replace(/\s*\(\s*not attached here\s*\)/gi, "");
  output = output.replace(/\s*\(\s*another module\s*\)/gi, "");

  // Repair glued sentence boundaries like "text.Next sentence" or "text!What".
  output = output.replace(/([.!?]["')\]]?)([A-Z])/g, "$1 $2");

  // Repair glued word+number boundaries like "and38" or "has12".
  output = output.replace(/([a-zA-Z])(\d{2,})\s/g, "$1 $2 ");

  // Ensure space around complete bold/italic spans glued to adjacent words.
  // Valid bold: **non-space-content** (content must not start or end with space).
  output = output.replace(/([a-zA-Z,;])(\*\*\S[^*\n]*?\S\*\*)/g, "$1 $2");
  output = output.replace(/(\*\*\S[^*\n]*?\S\*\*)([a-zA-Z])/g, "$1 $2");

  // Insert paragraph break before bold headings glued to preceding text (e.g., "300**Mexico**").
  // Exclude # to avoid breaking headings like "## **Bold Title**".
  output = output.replace(/([^\s\n#])(\*\*\S[^*\n]{1,}\*\*)/g, "$1\n\n$2");

  // Bold headings followed directly by a word/dash need a paragraph break after.
  // Require bold content to start and end with non-space (valid **bold**).
  output = output.replace(/(\*\*\S[^*\n]*?\S\*\*)(?=[A-Za-z0-9\u2013\u2014-])/g, "$1\n\n");

  // Ensure bullet items aren't glued to the previous sentence. The bullet's
  // first visible char may be a letter, "(", or an inline-markdown opener
  // (`**bold**`, `[link]`, `` `code` ``). The model's dominant list style is
  // "- **Name** — …", so `*`/`[`/backtick MUST be included — otherwise a
  // period-glued bold-led bullet ("…reply.- **Joseph**") stays inline while a
  // colon-glued one (handled above) becomes a real bullet, rendering one item
  // indented and the next not.
  output = output.replace(/([.!?:])\s*(-\s+[A-Za-z(*[`])/g, "$1\n$2");

  // Bold section labels (e.g., "**Title:**") should start their own paragraph.
  // Use [a-zA-Z0-9.!?;,)] to avoid matching bullet dashes like "- **Title:**".
  output = output.replace(
    /([a-zA-Z0-9.!?;,)])\s*(\*\*[A-Z]\S[^*\n]{0,80}:\*\*)(?=\s|$)/g,
    "$1\n\n$2",
  );


  // Final pass: re-collapse any bold pair that a \n\n-insertion rule above
  // re-broke across a line break (e.g. line "...glued bold heading..." can turn
  // `Summary**, **Characters` into `Summary\n\n**, **Characters`). Must be last
  // so the markers end up adjacent to their content for CommonMark flanking.
  output = collapseLineBrokenBold(output);

  return output;
}

function normalizeModuleLabel(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function parseOptionSuffix(value: string): { baseLabel: string; optionIndex: number | null } {
  const trimmed = value.trim();
  const match = /^(.*?)\s*\(\s*option\s+(\d+)\s*\)\s*$/i.exec(trimmed);
  if (!match) {
    return { baseLabel: trimmed, optionIndex: null };
  }
  const parsed = Number.parseInt(match[2], 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return { baseLabel: match[1].trim(), optionIndex: null };
  }
  return { baseLabel: match[1].trim(), optionIndex: parsed - 1 };
}

function stripLeadingOrderedListPrefix(value: string): string {
  const match = value.match(/^\s*\d{1,3}[.)]\s+(.+)$/);
  if (!match) return value.trim();
  return match[1].trim();
}

function escapeRegexLiteral(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function splitLeadingModuleLabel(
  text: string,
  labelByNormalized: Map<string, string>,
): { normalized: string; label: string; suffix: string } | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  for (const [normalized, label] of labelByNormalized.entries()) {
    if (!label) continue;
    const escaped = escapeRegexLiteral(label);
    const pattern = new RegExp(`^(${escaped})(\\s*(?::|-|–|—|\\(|\\[).*)?$`, "i");
    const match = trimmed.match(pattern);
    if (!match) continue;
    return {
      normalized,
      label,
      suffix: (match[2] || "").trimStart(),
    };
  }

  return null;
}

function flattenNodeText(node: React.ReactNode): string {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map((entry) => flattenNodeText(entry)).join("");
  }
  if (React.isValidElement(node)) {
    return flattenNodeText((node.props as { children?: React.ReactNode }).children);
  }
  return "";
}

function normalizeAssistantDisplayContent(input: string): string {
  if (!input) return input;
  let output = input.replace(/\r\n/g, "\n");

  // Strip tool output blocks that may have leaked into persisted messages
  output = output
    .replace(/<tool-output[^>]*>[\s\S]*?<\/tool-output>/gi, '')
    .trim();

  // Strip leaked tool-call JSON blocks (e.g., [{"tool_name": "...", "arguments": {...}}])
  // These are internal LLM tool invocations that should never appear in the chat.
  output = output.replace(/\[?\s*\{\s*"tool_name"\s*:\s*"[^"]*"\s*,\s*"arguments"\s*:\s*\{[^}]*\}\s*\}\s*\]?/g, "");

  // Strip {{XAVIOR_UI:...}}, {{XAVIOR_STATUS:...}}, {{XAVIOR_ACTIONS:...}} directives.
  // These are internal UI commands, not user-visible text.
  output = output.replace(/\{\{XAVIOR_[A-Z_]+:[\s\S]*?\}\}/g, "");
  // Also strip partial/incomplete directives (streaming edge case).
  output = output.replace(/\{\{XAVIOR_[A-Z_]+[\s\S]*$/g, "");

  // Convert basic HTML-ish responses to markdown-friendly text.
  if (/<\s*\/?\s*[a-z]/i.test(output)) {
    output = output.replace(/<\s*br\s*\/?\s*>/gi, "\n");
    output = output.replace(/<\/\s*(p|div|section|article|header|footer|blockquote|pre|ul|ol)\s*>/gi, "\n");
    output = output.replace(/<\s*li[^>]*>/gi, "\n- ");
    output = output.replace(/<\/\s*li\s*>/gi, "");
    output = output.replace(/<[^>]+>/g, "");
  }

  // Unwrap code fences (see stripCodeFences) so assistant prose never renders as
  // a monospace code box and a mid-stream unclosed fence can't stall the message,
  // then normalize the prose.
  output = normalizePlainAssistantSegment(stripCodeFences(output));

  return output.replace(/\n{3,}/g, "\n\n").trim();
}

// Extract the plain text content of a markdown <pre>'s children (a <code>
// element wrapping a raw string). Used so an indented "code block" can be shown
// as normal text rather than a monospace box.
function extractPreText(children: React.ReactNode): string {
  const first = React.Children.toArray(children)[0];
  if (React.isValidElement(first)) {
    const raw = (first.props as { children?: unknown }).children;
    if (Array.isArray(raw)) return raw.join("");
    if (typeof raw === "string") return raw;
    return String(raw ?? "");
  }
  return typeof first === "string" ? first : String(first ?? "");
}

const sanitizeSchema = {
  ...defaultSchema,
  tagNames: Array.from(
    new Set([
      ...(defaultSchema.tagNames ?? []),
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
      "hr",
      "input",
    ])
  ),
  protocols: {
    ...defaultSchema.protocols,
    href: [...(defaultSchema.protocols?.href ?? []), 'xavior'],
  },
  attributes: {
    ...defaultSchema.attributes,
    code: [
      ...(defaultSchema.attributes?.code ?? []),
      ["className", /^language-./],
    ],
    input: [
      ...(defaultSchema.attributes?.input ?? []),
      ["type", "checkbox"],
      "checked",
      "disabled",
    ],
  },
};

// A whole inline-code span that is JUST a bare email / phone. The model
// sometimes wraps an address in single backticks (the persona prompt shows
// addresses in backticks, so it imitates that) — which renders as a grey
// monospace pill instead of the usual bold, clickable address. These detect that
// exact case so `code()` can unwrap it. Deliberately anchored (^…$) so genuine
// inline code that merely CONTAINS an address is never unwrapped.
// Require a TLD-shaped final label (alpha, 2+) so a package/version spec like
// `react@18.2.0` (ends in `.0`) isn't unwrapped into a clickable email. (#code-review)
const BARE_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;
const BARE_PHONE_RE = /^[+(]?\d[\d\s().+-]{5,}$/;
// Shapes that pass BARE_PHONE_RE but are NOT phone numbers.
const ISO_DATE_RE = /^\d{4}-\d{1,2}-\d{1,2}$/; // 2024-01-15
const NUM_RANGE_RE = /^\d+\s*-\s*\d+$/; // 1000-2000, 100 - 200
const isBarePhone = (s: string): boolean => {
  if (!BARE_PHONE_RE.test(s)) return false;
  if (s.includes('.')) return false; // dotted token (version / IP), not a phone
  if (ISO_DATE_RE.test(s) || NUM_RANGE_RE.test(s)) return false;
  const digits = s.replace(/\D/g, '').length;
  if (digits < 7 || digits > 15) return false; // E.164 upper bound excludes long ids
  // Require phone-shaped structure: a country-code `+`, grouping (parens/space/
  // hyphen), or a 10+ digit run. A bare 7-9 digit token (order/account id) is not
  // a phone. (#code-review)
  return /[+()\s-]/.test(s) || digits >= 10;
};

/** Minimal shape of the hast node react-markdown passes as `node` on every component. */
interface HastNode {
  type?: string;
  tagName?: string;
  value?: string;
  properties?: { href?: unknown };
  children?: HastNode[];
}

function flattenHastText(node: HastNode | undefined): string {
  if (!node) return '';
  if (node.type === 'text') return node.value ?? '';
  return (node.children ?? []).map(flattenHastText).join('');
}

// Whether `node` is a `code` hast element whose ENTIRE text is a bare
// email/phone — the same shape `code()` below unwraps into its own clickable
// <button> (the same BARE_EMAIL_RE / isBarePhone check).
function isBareAddressCodeNode(node: HastNode): boolean {
  if (node.type !== 'element' || node.tagName !== 'code') return false;
  const text = flattenHastText(node).trim();
  return !!text && (BARE_EMAIL_RE.test(text) || isBarePhone(text));
}

function hasBareAddressCodeDescendant(node: HastNode | undefined): boolean {
  if (!node) return false;
  if (isBareAddressCodeNode(node)) return true;
  return (node.children ?? []).some(hasBareAddressCodeDescendant);
}

// A markdown link containing a bare email/phone in backticks ANYWHERE in its
// text — not just as the link's sole child. Both "reach [contact
// `addr@x.com` now](mailto:addr@x.com)" (mixed content) and "[*`addr@x.com`*]
// (mailto:addr@x.com)" (wrapped in emphasis/strong/etc.) render, at the hast
// level, as an `a` node with a `code` descendant somewhere under it — not
// necessarily its direct/only child. `code()` below unwraps THAT descendant
// into its own clickable <button> regardless of what wraps it. If `a()` ALSO
// wraps its (still-unrendered) `children` in a button, React ends up nesting
// a <button> inside that <button> once `code()` runs — invalid HTML, and it
// repeats on every render (#5542). react-markdown builds `children` as an
// unevaluated element description at this point (its `type` is the `code`/
// `em`/etc. component function, not a resolved DOM tag), so the nested
// button can't be detected by inspecting `children` — this walks the raw
// hast tree react-markdown passes as `node` instead, which is fully
// resolved. When a bare-address `code` descendant is found anywhere, callers
// render the WHOLE anchor's flattened text directly (dropping any em/strong
// decoration in that case, same trade-off as the sole-child case) so
// `code()` never runs for this subtree at all — exactly one button, ever.
function bareAddressLinkText(node: HastNode | undefined): string | null {
  if (!node || !hasBareAddressCodeDescendant(node)) return null;
  const text = flattenHastText(node);
  return text.trim() ? text : null;
}

// Whether hast subtree `node` contains a descendant that `a()`/`code()` below
// will render as an interactive <button>: an anchor whose href scheme `a()`
// turns into a button (xavior://object/, mailto:, tel:), or a bare
// email/phone `code` span `code()` unwraps into one. Mirrors those overrides'
// own conditions (including their click-handler gating) so it stays in
// lockstep with what will actually render. Used by BOTH `p()` and `li()` —
// the only two overrides that call `getClickableTarget` — to avoid wrapping
// a paragraph/item that already contains one of these buttons in a SECOND
// (module-label) button — deliberately over-inclusive on the ambiguous tel:
// case (a false positive here only skips the module-link enhancement; a
// false negative would silently destroy the existing button, which is the
// #5542 bug this replaces the non-functional React-element walk with —
// `children` at `p()`/`li()`-body time is the same kind of unevaluated
// element description described above, so a `type === "button"` check on it
// can never match a button an override like `a()`/`code()` hasn't rendered
// yet).
function hasButtonProducingDescendant(
  node: HastNode | undefined,
  clickHandlers: { onObjectClick: boolean; onEmailClick: boolean; onPhoneClick: boolean },
): boolean {
  if (!node) return false;
  if (node.type === 'element') {
    if (node.tagName === 'a') {
      const href = typeof node.properties?.href === 'string' ? node.properties.href : '';
      if (href.startsWith('xavior://object/') && clickHandlers.onObjectClick) return true;
      if (href.startsWith('mailto:') && clickHandlers.onEmailClick) return true;
      if (href.startsWith('tel:') && (clickHandlers.onPhoneClick || clickHandlers.onObjectClick)) return true;
    }
    if (isBareAddressCodeNode(node) && (clickHandlers.onEmailClick || clickHandlers.onPhoneClick)) return true;
  }
  return (node.children ?? []).some((child) => hasButtonProducingDescendant(child, clickHandlers));
}

export const AssistantMarkdown = memo(function AssistantMarkdown({
  content,
  className,
  moduleLabels,
  onModuleLabelClick,
  onObjectClick,
  onEmailClick,
  onPhoneClick,
  trailingInline,
}: Props) {
  const remarkPlugins = useMemo(() => [remarkGfm], []);
  const normalizedContent = useMemo(() => normalizeAssistantDisplayContent(content), [content]);
  const clickableModuleLabelMap = useMemo(() => {
    const labels = Array.isArray(moduleLabels) ? moduleLabels : [];
    const entries = labels
      .map((label) => {
        const trimmed = String(label || "").trim();
        return {
          normalized: normalizeModuleLabel(trimmed),
          label: trimmed,
        };
      })
      .filter((entry) => entry.normalized.length > 0 && entry.label.length > 0)
      .sort((a, b) => b.label.length - a.label.length);
    return new Map(entries.map((entry) => [entry.normalized, entry.label]));
  }, [moduleLabels]);
  const clickableModuleLabelSet = useMemo(() => {
    return new Set(clickableModuleLabelMap.keys());
  }, [clickableModuleLabelMap]);
  const rehypePlugins = useMemo(
    () => [[rehypeSanitize, sanitizeSchema]] as any,
    []
  );

  const components: Components = useMemo(() => {
    // Track repeated clickable labels in visual order so duplicate names
    // (e.g. "Tasks") can be mapped to the exact clicked occurrence.
    const clickableOccurrenceMap = new Map<string, number>();
    // Most-recently-rendered contact link in this message, used to route
    // click-on-email and click-on-phone to the same contact activity feed
    // instead of letting mailto:/tel: escape the app.
    let currentContactLink: { objectType: string; objectId: string } | null = null;
    const getClickableTarget = (
      rawText: string,
    ): { baseLabel: string; occurrenceIndex: number; suffix: string } | null => {
      if (!onModuleLabelClick || clickableModuleLabelSet.size === 0) return null;
      const flattened = rawText.trim();
      if (!flattened) return null;
      const withoutListPrefix = stripLeadingOrderedListPrefix(flattened);
      const { baseLabel, optionIndex } = parseOptionSuffix(withoutListPrefix);
      const prefixMatch = splitLeadingModuleLabel(baseLabel, clickableModuleLabelMap);
      const normalized = prefixMatch
        ? prefixMatch.normalized
        : normalizeModuleLabel(baseLabel);
      if (!normalized || !clickableModuleLabelSet.has(normalized)) return null;
      let occurrenceIndex = optionIndex;
      if (occurrenceIndex === null) {
        const nextOccurrence = (clickableOccurrenceMap.get(normalized) ?? 0) + 1;
        clickableOccurrenceMap.set(normalized, nextOccurrence);
        occurrenceIndex = nextOccurrence - 1;
      }
      if (prefixMatch) {
        return {
          baseLabel: prefixMatch.label,
          occurrenceIndex,
          suffix: prefixMatch.suffix,
        };
      }
      const canonicalLabel = clickableModuleLabelMap.get(normalized) || baseLabel;
      return { baseLabel: canonicalLabel, occurrenceIndex, suffix: "" };
    };

    return {
      a({ href, children, node, ...props }) {
        const safeHref = href ? safeUrlTransform(href) : undefined;
        if (!safeHref) return <span {...props}>{children}</span>;

        // A link containing a bare email/phone in backticks anywhere in its
        // text (e.g. "[`vincent@xavior.ai`](mailto:vincent@xavior.ai)", or
        // mixed/wrapped content like "[contact `v@x.ai` now](mailto:...)")
        // would otherwise nest a <button> (rendered below) around ANOTHER
        // <button> (code()'s bare-address unwrap, same file) once React
        // renders `children` — invalid HTML, flagged by React's DOM nesting
        // validator, and it repeats on every re-render (#5542). Render the
        // flattened text directly in that case so `code()` never runs for
        // this subtree — exactly one button, ever.
        const bareAddressText = bareAddressLinkText(node as HastNode | undefined);
        const linkContent: React.ReactNode = bareAddressText ?? children;

        // Handle xavior://object/ links — make them clickable to open the object
        const objectLink = parseObjectLink(safeHref);
        if (objectLink && onObjectClick) {
          if (objectLink.objectType === 'contact') {
            currentContactLink = objectLink;
          }
          return (
            <button
              type="button"
              className="assistant-md-object-link"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onObjectClick(objectLink.objectType, objectLink.objectId);
              }}
            >
              {linkContent}
            </button>
          );
        }

        // mailto: autolinked by remark-gfm — open the Gmail module's
        // compose view prefilled with this address, instead of escaping the
        // app to the OS mail client.
        if (safeHref.startsWith('mailto:') && onEmailClick) {
          const email = safeHref.slice('mailto:'.length).split('?')[0];
          return (
            <button
              type="button"
              className="assistant-md-object-link"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onEmailClick(email);
              }}
            >
              {linkContent}
            </button>
          );
        }

        // tel: autolinked by remark-gfm — no phone-dial flow yet; route to
        // the nearest preceding contact card if we have one, otherwise let
        // the OS handle it.
        if (safeHref.startsWith('tel:')) {
          if (onPhoneClick) {
            const phone = safeHref.slice('tel:'.length);
            return (
              <button
                type="button"
                className="assistant-md-object-link"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onPhoneClick(phone);
                }}
              >
                {linkContent}
              </button>
            );
          }
          if (currentContactLink && onObjectClick) {
            const target = currentContactLink;
            return (
              <button
                type="button"
                className="assistant-md-object-link"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onObjectClick(target.objectType, target.objectId);
                }}
              >
                {linkContent}
              </button>
            );
          }
        }

        const external = isProbablyExternalHref(safeHref);

        return (
          <a
            {...props}
            href={safeHref}
            target={external ? "_blank" : undefined}
            rel={external ? "noreferrer noopener" : undefined}
          >
            {linkContent}
          </a>
        );
      },

      strong({ children }) {
        return <strong>{children}</strong>;
      },

      p({ children, node }) {
        if (
          hasButtonProducingDescendant(node as HastNode | undefined, {
            onObjectClick: !!onObjectClick,
            onEmailClick: !!onEmailClick,
            onPhoneClick: !!onPhoneClick,
          })
        ) {
          return <p>{children}</p>;
        }
        const target = getClickableTarget(flattenNodeText(children));
        if (!target) return <p>{children}</p>;
        return (
          <p>
            <button
              type="button"
              className="assistant-md-module-link"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onModuleLabelClick?.(target.baseLabel, target.occurrenceIndex);
              }}
            >
              {target.baseLabel}
            </button>
            {target.suffix ? <span>{target.suffix}</span> : null}
          </p>
        );
      },

      li({ children, node }) {
        if (
          hasButtonProducingDescendant(node as HastNode | undefined, {
            onObjectClick: !!onObjectClick,
            onEmailClick: !!onEmailClick,
            onPhoneClick: !!onPhoneClick,
          })
        ) {
          return <li>{children}</li>;
        }
        const target = getClickableTarget(flattenNodeText(children));
        if (!target) return <li>{children}</li>;
        return (
          <li>
            <button
              type="button"
              className="assistant-md-module-link"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onModuleLabelClick?.(target.baseLabel, target.occurrenceIndex);
              }}
            >
              {target.baseLabel}
            </button>
            {target.suffix ? <span>{target.suffix}</span> : null}
          </li>
        );
      },

      pre({ children }) {
        // Fenced code is unwrapped upstream (stripCodeFences), so a <pre> here
        // comes from 4-space-indented lines the model used for layout. Render it
        // as normal wrapped text — never a monospace code box (which reads as
        // "code" to users and looks like a stall mid-stream). Line breaks are
        // preserved so the layout survives.
        const text = extractPreText(children).replace(/\n$/, "");
        return (
          <div
            className="assistant-md-block-text"
            style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
          >
            {text}
          </div>
        );
      },

      // Inline `code` only. `node` (and other react-markdown internals) are
      // intentionally dropped so they don't leak into the DOM as attributes
      // (e.g. node="[object Object]").
      code({ children, className }) {
        // A bare email/phone wrapped in backticks should read like the usual
        // bold, clickable address — NOT a grey monospace code pill. Unwrap it and
        // render it exactly like a remark-autolinked address (assistant-md-object-
        // link is font-weight:600 + clickable). Only when the WHOLE span is the
        // address; real inline code is left as <code>.
        const bare = typeof children === 'string'
          ? children
          : Array.isArray(children) && children.length === 1 && typeof children[0] === 'string'
            ? children[0]
            : null;
        const value = bare?.trim();
        if (value && BARE_EMAIL_RE.test(value)) {
          return onEmailClick ? (
            <button
              type="button"
              className="assistant-md-object-link"
              onClick={(event) => { event.preventDefault(); event.stopPropagation(); onEmailClick(value); }}
            >
              {value}
            </button>
          ) : <strong>{value}</strong>;
        }
        if (value && isBarePhone(value)) {
          return onPhoneClick ? (
            <button
              type="button"
              className="assistant-md-object-link"
              onClick={(event) => { event.preventDefault(); event.stopPropagation(); onPhoneClick(value); }}
            >
              {value}
            </button>
          ) : <strong>{value}</strong>;
        }
        return <code className={className}>{children}</code>;
      },

      table({ children }) {
        return (
          <div className="md-table">
            <table>{children}</table>
          </div>
        );
      },
    };
  }, [clickableModuleLabelMap, clickableModuleLabelSet, onModuleLabelClick, onObjectClick, onEmailClick, onPhoneClick]);

  // Post-render quality check: strip residual markdown chars that react-markdown
  // failed to parse (orphaned **, *, ##, etc. visible as literal text in the DOM).
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let node: Text | null;
    while ((node = walker.nextNode() as Text | null)) {
      const parent = node.parentElement;
      // Skip code/pre elements — markdown chars are intentional there.
      if (parent?.closest('code, pre')) continue;
      const text = node.textContent || '';
      const cleaned = text
        .replace(/^\*{1,2}\s*/g, '')          // leading ** or * (with optional space)
        .replace(/\s*\*{1,2}$/g, '')          // trailing ** or *
        .replace(/^#{1,6}\s*/g, '')           // leading # not parsed as heading
        .replace(/\s+\*{1,2}\s+/g, ' ')      // mid-text orphaned ** or * between spaces
        .replace(/^\*{1,2}$/g, '');           // text node that is just * or **
      if (cleaned !== text) {
        node.textContent = cleaned;
      }
    }
  }, [normalizedContent]);

  return (
    <div ref={containerRef} className={["assistant-md", trailingInline ? "has-trailing-inline" : "", className].filter(Boolean).join(" ")}>
      <Markdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        urlTransform={safeUrlTransform}
        components={components}
      >
        {normalizedContent}
      </Markdown>
      {trailingInline}
    </div>
  );
});
